import {computed, type ComputedRef, type Ref} from 'vue';
import {useI18n} from 'vue-i18n';
import type {JournalEntryDTO} from '@/ts/types/dto/journals-dto';

function keyOf(entry: JournalEntryDTO): string {
  return entry.userComponentId ?? entry.contentId;
}

/** A stretch of entries the list happens to show together, named if they share a session. */
export interface JournalSessionRun {
  /** The first entry's own key, so a run keeps its identity as the list grows. */
  key: string;
  /** The session's label, or null for a stretch belonging to no session the content still names. */
  heading: string | null;
  entries: JournalEntryDTO[];
}

/**
 * Groups the journal into the runs a session name can be put above.
 *
 * The list stays a diary: entries keep the order the backend sent and nothing is pulled out of
 * sequence to join its session. A run is therefore contiguity in the list rather than a day or a
 * session — entries of one session split by anything else form two runs, each named again, while
 * entries of one session that sit together form one run however many days they were written over.
 */
export function useJournalSessionRuns(
  entries: Ref<JournalEntryDTO[]>,
): ComputedRef<JournalSessionRun[]> {
  const {t} = useI18n();

  // The number is what orders the sessions; the name alone is translated and says nothing about
  // which came first. Both are resolved from the content at read time and a session that has left
  // it has neither, so an entry whose session is gone groups with its own but goes unnamed.
  function label({sessionId, sessionName, sessionNumber}: JournalEntryDTO): string | null {
    if (!sessionId || !sessionName || typeof sessionNumber !== 'number') {
      return null;
    }
    return t('journal_session_heading', {order: sessionNumber, name: sessionName});
  }

  return computed(() => entries.value.reduce<JournalSessionRun[]>((runs, entry, index) => {
    const open = runs[runs.length - 1];
    const previous = entries.value[index - 1];
    const heading = label(entry);
    // A boundary with no name on either side has nothing to show for itself, and the gap it
    // would open would read as a name that never arrived. That also covers an entry carrying no
    // session at all meeting one carrying an explicit null.
    if (open && (entry.sessionId === previous?.sessionId || (heading === null && open.heading === null))) {
      open.entries.push(entry);
      return runs;
    }
    runs.push({key: keyOf(entry), heading, entries: [entry]});
    return runs;
  }, []));
}
